import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { CustomElement } from "@cervval.js/client-html5-spa/navigation";
import { COLUMN_UID_ATTRIBUTE } from "@cervval.js/client-html5-ui/src/components/smartTable/smartTable";
import { PropertyConfig } from "@cervval.js/common/src/forms/propertyConfig";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { getLogService } from "@cervval.js/common/src/services/log.service";
import { getProp } from "@cervval.js/common/src/tools/any.tools";
import { objectKeys } from "@cervval.js/common/src/tools/object.tools";
import { MINUTES_IN_ONE_HOUR, formatMinutesHHMM } from "@cervval.js/common/src/tools/time.tools";
import { DAYS, TsDuration } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { durationValue } from "@cervval.js/common/src/tools/timestamps.tools.tsdate";
import { BookingWeek } from "@ehealth_tools/common-booking/src/data/booking.dto";
import { CalendarDay } from "@ehealth_tools/common-booking/src/data/booking.types";
import { addDurations, getClosestMinutes } from "@ehealth_tools/common-booking/src/tools/calendar.tools";
import { I18n, I18nWeekShifts } from "./weekSlots.component.i18n";

export type TimeShift = {
    start: TsDuration
}

export type WorkDaySlot = Partial<Record<DAYS, boolean>>;

export type SlotRow = {
    timeSlot: TimeShift,
    workDaySlot: WorkDaySlot
}

export type IndexedSlotRow = {
    row: SlotRow,
    index: number
}

export interface WeekSlotsOptions {

    currentWeek?: BookingWeek;

    /** Minimum Interval between two row in minutes */
    timeInterval: TsDuration;

    timeRange: {
        start: TsDuration,
        finish: TsDuration
    };

    onSelectDay?: (days: DAYS[]) => void;
}

export const defaultWeekShiftsOptions: WeekSlotsOptions = {
    /** 30 minutes by default */
    timeInterval: { hour: 0, minute: 30 },

    /** From 0 to 24 hour by default */
    timeRange: {
        start: { hour: 0 },
        finish: { hour: 24 }
    }
};


@CustomElement({
    name: "week-slots",
    template: require("./weekSlots.component.html"),
    style: require("./weekSlots.component.scss"),
    elementCssClasses: ["flex-grow-1"]
})
export class WeekSlots extends WebComponent<I18nWeekShifts> {

    @TypedWebComponentOptions(defaultWeekShiftsOptions)
    public readonly options!: WebComponentOptions<WeekSlotsOptions>;

    @TypedHtmlElements()
    private _components!: {
        weekShiftsTable: HTMLTableElement,
        tHeadRow: HTMLTableRowElement,
        tBody: HTMLTableSectionElement
    };

    private _rowMap: Map<number, IndexedSlotRow> = new Map();

    private _selectedWeekDays: DAYS[] = [];

    /** Init component */
    public override init(): void {
        for (const propertyConfig of this._getRowRangePropertyConfigs()) {
            const th = createHtmlElement("th", {
                attributes: {
                    [COLUMN_UID_ATTRIBUTE]: propertyConfig.propertyName,
                    id: `col-${propertyConfig.propertyName}`
                },
                innerHTML: propertyConfig.propertyName !== "timeStamp" ? propertyConfig.label : undefined
            });
            this._components.tHeadRow.appendChild(th);
        }

        this.initListener();
    }

    /** init component listeners */
    public initListener(): void {
        this._components.weekShiftsTable.tHead?.addEventListener("click", e => {
            const th = (e.target as HTMLElement).closest("th");
            if (!th) {
                return;
            }
            const columnUid = th?.getAttribute(COLUMN_UID_ATTRIBUTE);
            const day = columnUid ? parseInt(columnUid) : null;
            if (columnUid === "timeStamp") {
                if (e.ctrlKey) {
                    return;
                }
                this._selectedWeekDays = [];
                this.refreshColumnSelection();
                return;
            }
            if (e.ctrlKey && columnUid) {
                this._selectedWeekDays.push(parseInt(columnUid));
            }
            else if (e.shiftKey && day) {
                const minSelectedDay = this._selectedWeekDays.length > 0 ? this._selectedWeekDays.sort((a, b) => a - b)[0] : 0;
                const maxSelectedDay = this._selectedWeekDays.length > 0 ? this._selectedWeekDays.sort((a, b) => b - a)[0] : 8;
                for (let d = 1; d <= 7; d++) {
                    if (this._selectedWeekDays.includes(d)) {
                        continue;
                    }
                    if (day < minSelectedDay && d >= day && d < minSelectedDay) { // Fill from current to min
                        this._selectedWeekDays.push(d);
                    }
                    if (day > maxSelectedDay && d <= day && d > maxSelectedDay) { // Fill from max to current
                        this._selectedWeekDays.push(d);
                    }
                    if (day > minSelectedDay && day < maxSelectedDay && d > minSelectedDay && d < maxSelectedDay) { // Fill min to max
                        this._selectedWeekDays.push(d);
                    }
                }
            }
            else if (day) {
                this._selectedWeekDays = [day];
            }
            this.refreshColumnSelection();
            this.options.onSelectDay?.(this._selectedWeekDays);
        }, {
            capture: true
        });
    }

    /** Refresh column selection */
    public refreshColumnSelection(reset?: boolean): void {
        if (reset) {
            this._selectedWeekDays = [DAYS.MONDAY];
            this.options.onSelectDay?.(this._selectedWeekDays);
        }
        const headChildrenCollection = this._components.tHeadRow.children;
        for (let index = 0; index < headChildrenCollection.length; index++) {
            const element = headChildrenCollection[index];
            const day = element.getAttribute(COLUMN_UID_ATTRIBUTE);
            if (day) {
                toggleClass(element as HTMLElement, this._selectedWeekDays && this._selectedWeekDays.includes(parseInt(day)), "selectedWeekDay");
            }
        }
    }

    /** Refresh table with empty data*/
    private _clear(): void {
        // TODO Ideally but does not work :
        // const rowHeightToFit = Math.ceil((this.offsetHeight ?? 0) / this._emptyData().length);
        const rowHeightToFit = 80 / this._emptyData().length;

        this._components.tBody.innerHTML = "";
        const linePerHour = Math.floor(MINUTES_IN_ONE_HOUR / durationValue(this.options.timeInterval, "minute"));
        let i = 0;

        for (const rowItemData of this._emptyData()) {
            const tr = this._components.tBody.insertRow();
            tr.style.padding = "0px";
            tr.style.height = rowHeightToFit + "vh";
            if (i % linePerHour === 0) { // if there is a new hour to append to timestamp column
                const timeStampCell = tr.insertCell();
                timeStampCell.style.padding = "0px 10px 0px 0px";
                timeStampCell.rowSpan = linePerHour;
                const cellContent = createHtmlElement("div", {
                    cssClass: "cell-content",
                    innerHTML: formatMinutesHHMM(durationValue(rowItemData.timeSlot.start, "minute"))
                });
                timeStampCell.classList.add("timestamp-cell");
                timeStampCell.appendChild(cellContent);
            }
            i++;
            for (const weekDay of objectKeys(DAYS).filter(v => !isNaN(parseInt(v)))) {
                const td = tr.insertCell();
                td.style.padding = "0px";
                if (getProp(rowItemData.workDaySlot, weekDay)) {
                    td.classList.add("workedShift");
                }
            }
        }
    }

    /** Refresh table with the selected Calendar data */
    private _refreshCalendar(): void {
        const selectedCalendarWeek = this.options.currentWeek;
        if (!selectedCalendarWeek) {
            // getLogService().error("No selected Calendar");
            return;
        }
        for (const day of objectKeys(selectedCalendarWeek.week)) {
            const calendarDay = selectedCalendarWeek.week[day] as CalendarDay | undefined;
            if (calendarDay) {
                const timeslots = calendarDay.timeslots;
                for (const timeslot of timeslots) {
                    if (timeslot.finish > durationValue(this.options.timeRange.finish, "second") || timeslot.start < durationValue(this.options.timeRange.start, "second")) {
                        continue;
                    }
                    let timeShiftMinute = durationValue({ second: timeslot.start }, "minute");
                    while (timeShiftMinute < durationValue({ second: timeslot.finish }, "minute")) {
                        this.setWorkSlot(timeShiftMinute, { [day]: true });
                        timeShiftMinute += durationValue(this.options.timeInterval, "minute");

                    }

                }
            }
        }
    }

    /** Clears, refreshes column selection and populates with selected calendar*/
    public override refresh(reason?: string): void {
        this._clear();
        if (reason === "init") {
            return;
        }
        this.refreshColumnSelection();
        this._refreshCalendar();
    }

    /** Get Row range property Configs */
    private _getRowRangePropertyConfigs(): PropertyConfig[] {
        return [
            // first Column (TimeStamp)
            {
                propertyName: "timeStamp",
                label: "timeStamp",
                typeUid: PropertyConfigType.STRING
            },
            // Weekdays
            ...objectKeys(DAYS).filter(v => !isNaN(parseInt(v))).map(weekDay => {
                const label = getProp(I18n().weekDays, weekDay);
                return {
                    propertyName: weekDay,
                    label,
                    typeUid: PropertyConfigType.STRING
                } as PropertyConfig;
            })];
    }

    /** Generator to get row to append to the table */
    private *dateIterator(): Generator<TsDuration> {
        let duration = this.options.timeRange.start;
        const minuteStep = durationValue(this.options.timeInterval, "minute");
        while (durationValue(duration, "minute") < durationValue(this.options.timeRange.finish, "minute")) {
            yield duration;
            duration = addDurations(duration, { minute: minuteStep });
        }
    }

    /** Returns empty rows to insert in the table */
    private _emptyData(): SlotRow[] {
        return [...this.dateIterator()].map((duration, index) => {
            const shiftRow: SlotRow = {
                timeSlot: {
                    start: duration
                },
                workDaySlot: {

                }
            };
            this._rowMap.set(durationValue(duration, "minute"), { row: shiftRow, index });
            return shiftRow;
        });
    }

    /**
     * Set workShift for a row indexed by its minutes
     *
     * @param {number} minutes to retrieve row to set (get closest row)
     * @param {WorkDaySlot} workDaySlot days to set shift as worked or not
     */
    public setWorkSlot(minutes: number, workDaySlot: WorkDaySlot): void {
        const closestMinutes = getClosestMinutes(minutes, durationValue(this.options.timeInterval, "minute"), durationValue(this.options.timeRange.start, "minute"), durationValue(this.options.timeRange.finish, "minute"));
        if (closestMinutes == null) {
            getLogService().error("minutes: ", minutes, " are not in workshift range:", this.options.timeRange);
            return;
        }
        const slotRow = this._rowMap.get(closestMinutes);
        if (!slotRow) {
            getLogService().error("row not found for timeStamp: ", formatMinutesHHMM(minutes));
            return;
        }
        slotRow.row.workDaySlot = workDaySlot;
        this._refreshRow(slotRow);
    }

    /** Refresh cells class for a given row */
    private _refreshRow(indexedRow: IndexedSlotRow): void {
        const tr = this._components.tBody.children.item(indexedRow.index);
        if (!(tr instanceof HTMLTableRowElement)) {
            getLogService().error("table body child is not a row. Element : ", tr);
            return;
        }
        const isFirstElementTimeStamp = tr.children.item(0)?.className === "timestamp-cell";
        // ignore first column (timeStamp)
        for (let index = isFirstElementTimeStamp ? 1 : 0; index < tr.children.length; index++) {

            const cell = tr.children[index];
            if (!(cell instanceof HTMLTableCellElement)) {
                getLogService().error("table row child is not a cell. Element : ", cell);
                continue;
            }
            const isShiftWorked = getProp<boolean>(indexedRow.row.workDaySlot, isFirstElementTimeStamp ? index : index + 1);
            if (isShiftWorked !== undefined) {
                toggleClass(cell, isShiftWorked, "workedShift");
            }
        }
    }
}